import {
	Button,
	CssBaseline,
	GlobalStyles,
	Snackbar,
	StyledEngineProvider,
	ThemeProvider,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import moment from "moment-timezone";
import React, { type ReactNode, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { AuthProvider, Logout } from "./account";
import type { NavbarRendererProps } from "./components/navigation/NavbarMenuBar";
import Navigation from "./components/navigation/Navigation";
import type { AppReleaseApp } from "./configuration/pages/appReleases/models/AppRelease";
import { ConfigurationsType } from "./configurationMenu";
import { LogbookSettingsProvider } from "./context/LogbookSettingsContext";
import { ProvidersLoader } from "./context/ProvidersLoader";
import { UTLSettingsProvider } from "./context/UTLSettingContext";
import { AssetProvider } from "./context/assetContext";
import { UserProvider } from "./context/userContext";
import { ContextMenuMESFProvider, ShiftNavigatorProvider } from "./controls";
import { Alert } from "./controls/alert";
import { themeMESF } from "./css/themeMESF";
import {
	AppReleasesFeatureContext,
	ConfigurationContext,
	EmailTopicsFeatureContext,
	NavbarContext,
	OperatorsFeatureContext,
	RouterContext,
} from "./globalContext";
import { HelmetDexteelProvider } from "./helmet/HelmetDexteelContext";
import { useFrontendVersionCheck } from "./hooks/useFrontendVersionCheck";
import { RealtimeQueryInvalidation } from "./hooks/useRealtimeQueryInvalidation";
import type { LogbookSettingsState } from "./reducers/LogbookSettingsReducer";
import { TimeService } from "./services";
import { useClientTelemetry } from "./telemetry/useClientTelemetry";

export type { AppReleaseApp };

const timezone = TimeService.getInstance().getServerTimeZone();
moment.tz.setDefault(timezone);

const base =
	document.getElementsByTagName("base")[0].getAttribute("href") || "/";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			cacheTime: 1000 * 60,
		},
	},
});

interface Props {
	authentication: any;
	routes: () => ReactNode;
	navbar: React.ComponentType<NavbarRendererProps>;
	navbarTitle?: string;
	configurations: ConfigurationsType;
	showAreaSelector?: boolean;
	showTrendingsIcon?: boolean;
	showTrendingsV2Icon?: boolean;
	operatorsFeature?: boolean;
	emailTopicsFeature?: boolean;
	/** Mobile apps whose APK releases are managed in Configuration > App Releases. */
	appReleaseApps?: AppReleaseApp[];
	byPassHeaderRoutes?: string[];
	plantAssetId?: number;
	theme?: any;
	logbookSettings?: Partial<LogbookSettingsState>;
	enableFrontendVersionCheck?: boolean;
	frontendVersionCheckIntervalMs?: number;
	frontendVersionEndpoint?: string;
	enableClientTelemetry?: boolean;
}

const NO_APP_RELEASE_APPS: AppReleaseApp[] = [];

const ClientTelemetry = ({ enabled }: { enabled: boolean }) => {
	useClientTelemetry(enabled);
	return null;
};

function MESFMain({
	authentication,
	routes,
	navbar,
	navbarTitle = "MESF",
	configurations,
	showAreaSelector = false,
	showTrendingsV2Icon = true,
	operatorsFeature = false,
	emailTopicsFeature = false,
	appReleaseApps = NO_APP_RELEASE_APPS,
	byPassHeaderRoutes = [],
	plantAssetId = 1,
	theme = themeMESF,
	logbookSettings = {},
	enableFrontendVersionCheck = true,
	frontendVersionCheckIntervalMs = 60_000,
	frontendVersionEndpoint = "/frontend/version",
	enableClientTelemetry = true,
}: Props) {
	const [isVersionMessageDismissed, setIsVersionMessageDismissed] =
		useState(false);

	const { isUpdateAvailable } = useFrontendVersionCheck({
		enabled: enableFrontendVersionCheck,
		intervalMs: frontendVersionCheckIntervalMs,
		endpoint: frontendVersionEndpoint,
	});

	const showVersionAlert = isUpdateAvailable && !isVersionMessageDismissed;

	return (
		<>
			<StyledEngineProvider injectFirst>
				<ThemeProvider theme={theme}>
					<CssBaseline />
					<GlobalStyles
						styles={(activeTheme) => ({
							":root": {
								"--mesf-page-background":
									activeTheme.palette.background.default,
							},
						})}
					/>
					<HelmetProvider>
						<HelmetDexteelProvider navbarTitle={navbarTitle}>
							<QueryClientProvider client={queryClient}>
								<RealtimeQueryInvalidation />
								<AuthProvider authConfig={authentication}>
									<LocalizationProvider
										dateAdapter={AdapterMoment}
										dateLibInstance={moment}
									>
										<UserProvider>
											<UTLSettingsProvider>
												<ProvidersLoader>
													<LogbookSettingsProvider
														logbookSettings={logbookSettings}
													>
														<AssetProvider plantAssetId={plantAssetId}>
															<RouterContext.Provider value={routes}>
																<NavbarContext.Provider value={navbar}>
																	<ConfigurationContext.Provider
																		value={configurations}
																	>
																		<OperatorsFeatureContext.Provider
																			value={operatorsFeature}
																		>
																			<EmailTopicsFeatureContext.Provider
																				value={emailTopicsFeature}
																			>
																				<AppReleasesFeatureContext.Provider
																					value={appReleaseApps}
																				>
																					<ShiftNavigatorProvider>
																						<ContextMenuMESFProvider>
																							<Router basename={base}>
																								<ClientTelemetry
																									enabled={
																										enableClientTelemetry
																									}
																								/>
																								<Routes>
																									<Route
																										path="/logout"
																										element={<Logout />}
																									/>
																								</Routes>
																								<Navigation
																									showAreaSelector={
																										showAreaSelector
																									}
																									navbarTitle={navbarTitle}
																									byPassHeaderRoutes={
																										byPassHeaderRoutes
																									}
																									showTrendingsV2Icon={
																										showTrendingsV2Icon
																									}
																								/>
																							</Router>
																						</ContextMenuMESFProvider>
																					</ShiftNavigatorProvider>
																				</AppReleasesFeatureContext.Provider>
																			</EmailTopicsFeatureContext.Provider>
																		</OperatorsFeatureContext.Provider>
																	</ConfigurationContext.Provider>
																</NavbarContext.Provider>
															</RouterContext.Provider>
														</AssetProvider>
													</LogbookSettingsProvider>
												</ProvidersLoader>
											</UTLSettingsProvider>
										</UserProvider>
									</LocalizationProvider>
								</AuthProvider>
							</QueryClientProvider>
						</HelmetDexteelProvider>
					</HelmetProvider>
				</ThemeProvider>
			</StyledEngineProvider>
			<Snackbar
				open={showVersionAlert}
				onClose={(_, reason) => {
					if (reason === "clickaway") {
						return;
					}
					setIsVersionMessageDismissed(true);
				}}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					severity="info"
					action={
						<Button
							color="inherit"
							size="small"
							onClick={() => window.location.reload()}
						>
							Reload
						</Button>
					}
				>
					There is a new version. Please reload the page.
				</Alert>
			</Snackbar>
		</>
	);
}

export { MESFMain };
